/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from "react";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import UserInteractionsService from "../../services/interactions/Interactions";

export type InteractionSectionKey = "upcoming" | "past";

export type InteractionSort = {
  sort_field: string;
  sort_order: string;
};

export type InteractionWindow = {
  startTime: string;
  endTime: string;
};

export type InteractionSectionFilters = {
  debouncedSearch: string;
  rating: string;
  isHighRadiusUser: boolean;
  external: boolean;
  selectedTab: string;
  baseProfileOrgId: string;
  employeePorId: string;
  dateFilterType?: string;
  impersonatingPorId?: string;
  accessLevel?: string;
  taggedAccountIds: string[];
  taggedOpportunityIds: string[];
  includeOpportunityFilter: boolean;
};

type UseInteractionSectionArgs = {
  windowRange: InteractionWindow | null;
  enabled: boolean;
  fetchList: boolean;
  shouldCallCount: boolean;
  initialPageNumber: number;
  initialPageSize: number;
  initialSort: InteractionSort;
  filters: InteractionSectionFilters;
  buildRows: (
    interactions: any[],
    pageSize: number,
    pageNumber: number,
    sort: InteractionSort
  ) => any[];
};

export const getInteractionWindows = (
  startDate: string,
  endDate: string,
  now: Date
): { past: InteractionWindow | null; upcoming: InteractionWindow | null } => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const startMs = start.getTime();
  const endMs = end.getTime();
  const nowMs = now.getTime();
  if (!startDate || !endDate || Number.isNaN(startMs) || Number.isNaN(endMs) || startMs > endMs) {
    return { past: null, upcoming: null };
  }

  return {
    past:
      startMs < nowMs
        ? {
            startTime: startDate,
            endTime: endMs < nowMs ? endDate : new Date(nowMs - 1).toISOString(),
          }
        : null,
    upcoming:
      endMs >= nowMs
        ? {
            startTime: startMs >= nowMs ? startDate : new Date(nowMs).toISOString(),
            endTime: endDate,
          }
        : null,
  };
};

const useInteractionSection = ({
  windowRange,
  enabled,
  fetchList,
  shouldCallCount,
  initialPageNumber,
  initialPageSize,
  initialSort,
  filters,
  buildRows,
}: UseInteractionSectionArgs) => {
  const { showSnackbar } = useDisplaySnackbar();
  const showSnackbarRef = useRef(showSnackbar);
  showSnackbarRef.current = showSnackbar;
  const [pageNumber, setPageNumber] = useState(initialPageNumber);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sort, setSort] = useState<InteractionSort>(initialSort);
  const [rows, setRows] = useState<any[]>([]);
  const buildRowsRef = useRef(buildRows);
  buildRowsRef.current = buildRows;

  const canFetchCount = enabled && Boolean(windowRange);
  const canFetchList = canFetchCount && fetchList;
  const {
    debouncedSearch,
    rating,
    isHighRadiusUser,
    external,
    selectedTab,
    baseProfileOrgId,
    employeePorId,
    dateFilterType,
    impersonatingPorId,
    accessLevel,
    taggedAccountIds,
    taggedOpportunityIds,
    includeOpportunityFilter,
  } = filters;

  const {
    isLoading: isListLoading,
    isFetching: isListFetching,
    isSuccess: isListSuccess,
    isError: isListError,
    error: listError,
    data: listData,
  } = UserInteractionsService.useGetUserInteractions(
    {
      startTime: windowRange?.startTime ?? "",
      endTime: windowRange?.endTime ?? "",
      offset: pageNumber,
      limit: pageSize,
      interactionTitle: debouncedSearch || "",
      ...(isHighRadiusUser
        ? { abstractiveScore: rating }
        : { overallSentiment: rating || "" }),
      ratingSource: "",
      isExternal: external,
      sortBy: sort.sort_field,
      sortOrder: sort.sort_order,
      isTeamInteraction:
        selectedTab === "myInteraction" || selectedTab === "sharedWithMe" ? false : true,
      ...(selectedTab === "teamInteraction" ? { userPorId: baseProfileOrgId } : {}),
      employeePorId,
      ...(selectedTab === "sharedWithMe" ? { sharedWithMeInteractions: true } : {}),
      ...(selectedTab === "sharedByMe" ? { sharedByMeInteractions: true } : {}),
      isTeamOnlyInteraction:
        selectedTab === "myInteraction" || selectedTab === "sharedWithMe" ? false : true,
      isOnlyInteraction: selectedTab === "myInteraction",
      searchValue: debouncedSearch || "",
      ...(dateFilterType ? { dateFilterType } : {}),
      impersonatingPorId,
      accessLevel,
      ...(taggedAccountIds.length > 0 && {
        taggedAccountSubOrgId: taggedAccountIds,
      }),
      ...(includeOpportunityFilter &&
        taggedOpportunityIds.length > 0 && {
          taggedOpportunityId: taggedOpportunityIds,
        }),
    },
    canFetchList
  );

  const {
    isLoading: isCountLoading,
    isFetching: isCountFetching,
    data: countData,
  } = UserInteractionsService.useGetUserInteractionCount(
    {
      startTime: windowRange?.startTime ?? "",
      endTime: windowRange?.endTime ?? "",
      ...(isHighRadiusUser
        ? { abstractiveScore: rating }
        : { overallSentiment: rating || "" }),
      ratingSource: "",
      isExternal: external,
      ...(selectedTab === "teamInteraction" ? { userPorId: baseProfileOrgId } : {}),
      employeePorId,
      ...(selectedTab === "sharedWithMe" ? { sharedWithMeInteractions: true } : {}),
      ...(selectedTab === "sharedByMe" ? { sharedByMeInteractions: true } : {}),
      isTeamOnlyInteraction: selectedTab === "teamInteraction",
      isTeamInteraction:
        selectedTab === "myInteraction" || selectedTab === "sharedWithMe" ? false : true,
      isOnlyInteraction: selectedTab === "myInteraction",
      searchValue: debouncedSearch || "",
      impersonatingPorId,
      accessLevel,
      ...(taggedAccountIds.length > 0 && {
        taggedAccountSubOrgId: taggedAccountIds,
      }),
      ...(includeOpportunityFilter &&
        taggedOpportunityIds.length > 0 && {
          taggedOpportunityId: taggedOpportunityIds,
        }),
    },
    canFetchCount && shouldCallCount
  );

  useEffect(() => {
    if (!canFetchCount) {
      setRows([]);
      return;
    }
    if (!canFetchList) return;
    if (isListSuccess && listData?.data) {
      setRows(buildRowsRef.current(listData.data, pageSize, pageNumber, sort));
    }
    if (isListError) {
      showSnackbarRef.current("error", listError?.message);
    }
  }, [
    canFetchCount,
    canFetchList,
    isListSuccess,
    isListError,
    listData,
    listError,
    pageSize,
    pageNumber,
    sort,
  ]);

  const totalRecord = !canFetchCount
    ? 0
    : shouldCallCount
      ? countData?.totalCount
      : listData?.totalRecords;

  const isLoading =
    canFetchList &&
    (isListLoading ||
      isListFetching ||
      (shouldCallCount && (isCountLoading || isCountFetching)));

  const onPaginationChange = (pageIndex: number) => {
    setPageNumber(pageIndex + 1);
  };

  const onSortingChange = (sorting: any[]) => {
    if (sorting && sorting.length > 0) {
      setSort({
        sort_field: sorting[0].id,
        sort_order: sorting[0].desc === true ? "desc" : "asc",
      });
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  return {
    rows,
    setRows,
    totalRecord: totalRecord as number | undefined,
    isLoading,
    pageNumber,
    setPageNumber,
    pageSize,
    sort,
    onPaginationChange,
    onSortingChange,
    handlePageSizeChange,
    interactionsData: listData,
  };
};

export default useInteractionSection;
